import { useMutation, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, Save } from "lucide-react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  CreateLoyaltyProgramData,
  loyaltyProgramService,
} from "../../services/loyalty-program.service";

export default function CreateLoyaltyProgramPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [formData, setFormData] = useState<CreateLoyaltyProgramData>({
    name: "",
    description: "",
    pointsPerAmount: 1,
    amountPerPoint: 1,
    minRedeemPoints: undefined,
    maxRedeemPercent: undefined,
    expiryDays: undefined,
  });

  const createMutation = useMutation({
    mutationFn: loyaltyProgramService.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["loyalty-programs"] });
      navigate("/loyalty-programs");
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (
      !formData.name ||
      !formData.pointsPerAmount ||
      !formData.amountPerPoint
    ) {
      return;
    }
    createMutation.mutate(formData);
  };

  const handleNumberChange = (
    key: keyof CreateLoyaltyProgramData,
    value: string,
  ) => {
    setFormData((prev) => ({
      ...prev,
      [key]: value === "" ? undefined : Number(value),
    }));
  };

  return (
    <div className="p-6">
      <div className="mb-6">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-800"
        >
          <ArrowLeft className="h-4 w-4" />
          Back
        </button>
      </div>

      <div className="max-w-3xl">
        <h1 className="text-2xl font-bold mb-6">Add Loyalty Program</h1>

        <form
          onSubmit={handleSubmit}
          className="bg-white rounded-lg shadow p-6 space-y-4"
        >
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Program Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              required
              value={formData.name}
              onChange={(e) =>
                setFormData({ ...formData, name: e.target.value })
              }
              className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Points Per Amount <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                min="0"
                step="0.01"
                value={formData.pointsPerAmount}
                onChange={(e) =>
                  handleNumberChange("pointsPerAmount", e.target.value)
                }
                required
                className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Amount Per Point <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                min="0"
                step="0.01"
                value={formData.amountPerPoint}
                onChange={(e) =>
                  handleNumberChange("amountPerPoint", e.target.value)
                }
                required
                className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Min Redeem Points
              </label>
              <input
                type="number"
                min="0"
                value={formData.minRedeemPoints ?? ""}
                onChange={(e) =>
                  handleNumberChange("minRedeemPoints", e.target.value)
                }
                className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Max Redeem Percent
              </label>
              <input
                type="number"
                min="0"
                max="100"
                value={formData.maxRedeemPercent ?? ""}
                onChange={(e) =>
                  handleNumberChange("maxRedeemPercent", e.target.value)
                }
                className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Expiry Days
              </label>
              <input
                type="number"
                min="0"
                value={formData.expiryDays ?? ""}
                onChange={(e) =>
                  handleNumberChange("expiryDays", e.target.value)
                }
                className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Description
            </label>
            <textarea
              value={formData.description || ""}
              onChange={(e) =>
                setFormData({ ...formData, description: e.target.value })
              }
              rows={3}
              className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div className="flex gap-3 pt-4">
            <button
              type="submit"
              disabled={createMutation.isPending}
              className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              <Save className="h-4 w-4" />
              {createMutation.isPending ? "Saving..." : "Save Program"}
            </button>
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="px-4 py-2 border rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
          </div>

          {createMutation.isError && (
            <div className="text-red-600 text-sm">
              {(createMutation.error as any)?.response?.data?.message ||
                "Error creating loyalty program. Please try again."}
            </div>
          )}
        </form>
      </div>
    </div>
  );
}
